import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, TriangleAlert } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { ROLE_VALUES } from '@aeris/contracts'
import { ROLES, canGrantRole, type Role } from '@aeris/permissions'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
} from '@aeris/ui'

import { ApiError, addMember } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

import { ROLE_DESCRIPTIONS, ROLE_LABELS } from './role-labels.ts'

/**
 * Give somebody access.
 *
 * Two states in one dialog: the form, and then the handover. The handover is
 * not a toast and cannot be dismissed by clicking outside, because the password
 * on it exists nowhere else — the server hashed it and forgot it, exactly as it
 * does for a password somebody chose themselves. Losing it means a reset, not a
 * lookup, and an interface that lets it scroll away is an interface that
 * produces that reset.
 *
 * The role list is filtered by `canGrantRole` from `@aeris/permissions` — the
 * same function the API refuses with. Filtering here only avoids offering
 * something that would be refused; the refusal is the server's.
 */

const schema = z.object({
  displayName: z.string().trim().min(1, 'Informe o nome').max(200),
  email: z
    .string()
    .trim()
    .min(1, 'Informe o e-mail')
    .max(320)
    .refine(
      (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value),
      'Informe um e-mail válido'
    ),
  role: z.enum(ROLE_VALUES),
})

type FormValues = z.infer<typeof schema>

/** The handover panel, shown once and never reproducible. */
const Handover = ({
  name,
  email,
  password,
  onClose,
}: {
  name: string
  email: string
  password: string
  onClose: () => void
}): ReactNode => {
  const [copied, setCopied] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <p
        role="alert"
        className="flex items-start gap-2 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)"
      >
        <TriangleAlert
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
        />
        <span>
          Esta senha aparece <strong>uma única vez</strong>. Ela não fica
          guardada e não pode ser consultada depois. Entregue-a a {name} agora;
          o sistema vai pedir que {name.split(' ')[0]} troque a senha no
          primeiro acesso.
        </span>
      </p>

      <div className="flex flex-col gap-1">
        <span className="text-(length:--text-xs) text-(--text-muted)">
          E-mail
        </span>
        <span className="font-(--font-mono) text-(length:--text-sm) text-(--text-primary)">
          {email}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-(length:--text-xs) text-(--text-muted)">
          Senha temporária
        </span>
        <div className="flex items-center gap-2">
          <code
            data-testid="temporary-password"
            className="flex-1 select-all rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) px-3 py-2 font-(--font-mono) text-(length:--text-sm) tracking-wider text-(--text-primary)"
          >
            {password}
          </code>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leadingIcon={
              copied ? (
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <Copy aria-hidden="true" className="h-3.5 w-3.5" />
              )
            }
            onClick={() => {
              /**
               * Best effort, and deliberately not more than that.
               *
               * The Clipboard API is unavailable outside a secure context and
               * can be refused by permission policy — a real case on a branch
               * machine served over plain HTTP. Either way the password is
               * still on screen and `select-all` makes it one click to select,
               * so a failure here costs a copy button, not the handover.
               */
              try {
                void navigator.clipboard.writeText(password).then(
                  () => {
                    setCopied(true)
                  },
                  () => {
                    setCopied(false)
                  }
                )
              } catch {
                setCopied(false)
              }
            }}
          >
            {copied ? 'Copiada' : 'Copiar'}
          </Button>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <Button type="button" variant="primary" onClick={onClose}>
          Já anotei
        </Button>
      </div>
    </div>
  )
}

export type MemberFormProps = {
  onClose: () => void
}

export const MemberForm = ({ onClose }: MemberFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const [formError, setFormError] = useState<string | null>(null)
  const [handover, setHandover] = useState<{
    name: string
    email: string
    password: string
  } | null>(null)

  const actorRole = session?.context?.role ?? null

  const grantable: readonly Role[] =
    actorRole === null
      ? []
      : ROLES.filter((role) => canGrantRole(actorRole, role))

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: '',
      email: '',
      // The least privileged role that can be granted: adding somebody should
      // start from "can do the least" and be raised deliberately.
      role: grantable.at(-1) ?? 'cashier',
    },
  })

  const selectedRole = useWatch({ control, name: 'role' })

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => await addMember(values),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['team'] })

      if (response.temporaryPassword === null) {
        // An account that already existed keeps its own password, so there is
        // nothing to hand over and nothing to warn about.
        onClose()
        return
      }

      setHandover({
        name: response.member.displayName,
        email: response.member.email,
        password: response.temporaryPassword,
      })
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível dar acesso. Tente novamente.'
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    // Um envio por vez (F248): o segundo, com o primeiro andando, criaria outro.
    if (mutation.isPending) return
    setFormError(null)
    mutation.mutate(values)
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // While a password is on screen, the only way out is the button that
        // acknowledges it.
        if (!next && handover === null) onClose()
      }}
    >
      <DialogContent
        title={handover === null ? 'Dar acesso' : 'Senha temporária'}
        description={
          handover === null
            ? 'A pessoa recebe uma senha temporária e troca no primeiro acesso.'
            : 'Anote agora: ela não será mostrada de novo.'
        }
        className="max-w-lg"
      >
        {handover === null ? (
          <form
            onSubmit={(event) => {
              void onSubmit(event)
            }}
            noValidate
            className="flex flex-col gap-4"
          >
            {formError === null ? null : (
              <p
                role="alert"
                className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
              >
                {formError}
              </p>
            )}

            <Field label="Nome" required error={errors.displayName?.message}>
              {(field) => (
                <Input
                  {...field}
                  {...register('displayName')}
                  autoFocus
                  placeholder="Maria Silva"
                />
              )}
            </Field>

            <Field
              label="E-mail"
              required
              error={errors.email?.message}
              hint="Será o login desta pessoa"
            >
              {(field) => (
                <Input
                  {...field}
                  {...register('email')}
                  type="email"
                  autoComplete="off"
                  placeholder="maria@empresa.com.br"
                />
              )}
            </Field>

            {/* The hint follows the selection, so what a role means is read at
                the moment it is chosen rather than looked up elsewhere.
                `useWatch` rather than `watch()`: the latter returns a function
                the React Compiler cannot memoise, and it would silently skip
                optimising this whole component. */}
            <Field
              label="Papel"
              required
              error={errors.role?.message}
              hint={ROLE_DESCRIPTIONS[selectedRole]}
            >
              {(field) => (
                <Select {...field} {...register('role')}>
                  {grantable.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                variant="primary"
                loading={mutation.isPending}
              >
                Dar acesso
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <Handover {...handover} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  )
}
